/**
 * The "floor plan" a rule reacts to. It is a product in the backend, but in this
 * feature a product *is* the alternate floor plan, so the UI labels it that way.
 */
export interface DsmRoomReplacementRuleTriggerProduct {
  id: string;
  code: string;
  description: string;
}

export interface DsmRoomReplacementRuleRoom {
  locationId: string;
  locationCode: string;
  locationName: string;
}

export interface DsmRoomReplacementRule {
  id: string;
  modelId: string;
  elevationId: string;
  triggerProduct: DsmRoomReplacementRuleTriggerProduct;
  replacedRooms: DsmRoomReplacementRuleRoom[];
  newRooms: DsmRoomReplacementRuleRoom[];
  isActive: boolean;
  isExecuted: boolean;
  pendingLotCount: number;
  executedLotCount: number;
}

/** Global list item — adds the names the All Rules table shows. */
export interface DsmRoomReplacementRuleSummary extends DsmRoomReplacementRule {
  projectId: string;
  projectName: string;
  modelName: string;
  elevationCode: string;
  createdAt: string;
  createdByName: string | null;
}

/** Every filter the global list endpoint accepts; all are exact-match ids. */
export interface DsmRoomReplacementRulesQuery {
  projectId?: string | null;
  modelId?: string | null;
  elevationId?: string | null;
  // triggerProductId?: string | null; // Floor Plan filter removed from the list.
  includeInactive?: boolean;
}

/** What both create and update send; the room lists are replaced wholesale. */
interface DsmRoomReplacementRuleBody {
  triggerProductId: string;
  isActive: boolean;
  replacedRoomLocationIds: string[];
  newRoomLocationIds: string[];
}

/**
 * Create is a global endpoint, so the model rides in the body rather than the path, and
 * it takes *many* elevations: one rule is created per elevation, all sharing this
 * trigger and these room lists.
 */
export interface CreateDsmRoomReplacementRulesPayload extends DsmRoomReplacementRuleBody {
  modelId: string;
  elevationIds: string[];
}

/**
 * Update is global too — keyed only on the rule id — so the model comes in the body
 * alongside the single elevation this rule applies to.
 */
export interface UpdateDsmRoomReplacementRulePayload extends DsmRoomReplacementRuleBody {
  modelId: string;
  elevationId: string;
}

/** One copy destination. The route names the rule, so every model here is a target. */
export interface DsmRoomReplacementRuleCopyTarget {
  modelId: string;
  elevationId: string;
}

export enum DsmRoomReplacementRuleCopyStatus {
  Copied = 'Copied',
  Skipped = 'Skipped'
}

/**
 * Every target is reported back, copied or not, and a skip always carries the reason
 * it was skipped — a target may be missing one of the rooms, already hold an active
 * rule for the floor plan, or not offer the floor plan at all.
 */
export interface DsmRoomReplacementRuleCopyResult extends DsmRoomReplacementRuleCopyTarget {
  status: DsmRoomReplacementRuleCopyStatus;
  reason: string | null;
  pendingLotCount: number;
}

export interface DsmRoomReplacementRuleCopyResponse {
  results: DsmRoomReplacementRuleCopyResult[];
}

/** Duplicate pre-check for a trigger on a model + elevation. */
export interface DsmRoomReplacementRuleValidation {
  elevationId: string;
  canCreate: boolean;
  message: string | null;
  /**
   * The active rule already holding this trigger, so the form can offer to open or copy
   * it; null whenever the combination is free (and on any other kind of refusal).
   */
  ruleId: string | null;
}

/**
 * Get-by-id shape (backend `RoomReplacementRuleDetailDto : RoomReplacementRuleListDto`).
 *
 * ⚠ It is **not** the global list item: it carries `modelId` and `elevationId` but **no
 * `projectId` and no display names**. That is why the edit route still has to carry the
 * project id — the rooms endpoint is project-scoped and nothing in this payload names it.
 */
export interface DsmRoomReplacementRuleDetail extends DsmRoomReplacementRule {
  createdAt: string;
  lastModified: string | null;
  createdByUserId: string;
  updatedByUserId: string | null;
  createdByName: string | null;
  updatedByName: string | null;
}

export interface DsmRoomReplacementRulePendingLot {
  dealId: string;
  lotId: string;
  lotNo: string;
  buyer: string | null;
  addendumId: string;
  addendumSequenceNumber: number;
  triggerProductCode: string | null;
  triggerProductDescription: string | null;
}

/**
 * One lot a rule has already run on (`GET /room-replacement-rules/{id}/executed-lots`).
 * Grouped by lot, so the list length matches `executedLotCount`.
 */
export interface DsmRoomReplacementRuleExecutedLot {
  /** Null on a lot-only (LOA) addendum. */
  dealId: string | null;
  lotId: string;
  lotNo: string;
  buyer: string | null;
  addendumId: string;
  addendumSequenceNumber: number;
  executedAt: string;
  executedByName: string | null;
}

export interface DsmPendingLocationRule {
  ruleId: string;
  triggerProductCode: string;
  triggerProductDescription: string;
}
